import React, { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { getScenarioLevers, runAuditScenario } from "@/lib/api";
import MetricDefinitions from "@/components/MetricDefinitions";

// A cost line's share, on the base its working tolerance is defined on. Mirrors
// _pct_of_base / _pct_cell in backend/report_pdf.py so the PDF and this page
// print the same thing. Reports stored before 28 Sep 2026 carry only
// pct_of_net_sales, and every line in them was scored on net sales.
const BASE_NOTE = "Refunds are measured against gross sales, the base a return rate is quoted on; every other line is measured against net sales.";
const basePct = (l) => (l.pct_of_base ?? l.pct_of_net_sales);
// Mirrors _working_band_note in backend/report_pdf.py.
const workingBandNote = (implied) =>
  "These are separate ceilings for each line, not a budget. No business carries every line at its ceiling: " +
  `a business sitting on every band that applies to it ${implied < 0 ? "would still return" : "would return"} ${implied}% of net sales.`;
const pctOfBase = (l) => `${basePct(l)}% ${l.base_label || "of net sales"}`;
const pctCell = (l) => (l.base === "gross_sales" ? `${basePct(l)}% (gross)` : `${basePct(l)}%`);

function Section({ tag, title, subtitle, children, testid, badge }) {
  return (
    <section data-testid={testid} className="card-cream">
      <header className="grid md:grid-cols-[220px_1fr] border-b border-[#ddd8ce]">
        <div className="p-6 md:p-8 border-r border-[#ddd8ce]">
          <div className="tick">{tag}</div>
        </div>
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#171717] leading-tight">{title}</h3>
            {badge}
          </div>
          {subtitle && (
            <p className="mt-3 font-mono-tech text-[12px] text-[#404040] max-w-2xl leading-relaxed">{subtitle}</p>
          )}
        </div>
      </header>
      <div className="p-6 md:p-8">{children}</div>
    </section>
  );
}

// A "modelled" result is derived, not measured - the label is a heading-level
// badge everywhere one appears, not a sentence buried in a paragraph, so it
// can't be missed by a reader skimming past the prose.
function ModelledBadge({ children = "Modelled, not measured" }) {
  return (
    <span className="tick text-[#9a3412] bg-[rgba(154,52,18,0.1)] px-2.5 py-1 rounded-full shrink-0 whitespace-nowrap">
      {children}
    </span>
  );
}

const sevColor = (s) => (s === "high" ? "text-[#9a3412] font-medium" : s === "medium" ? "text-[#8a5a3a]" : "text-[#404040]");

const money = (v, cur) => {
  if (v === null || v === undefined) return "data_unavailable";
  const sign = v < 0 ? "−" : "";
  return `${sign}${cur ? cur + " " : ""}${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

// Every bar floats on one shared scale from where the running total stood to
// where it stood after that line, so bar position and length are directly
// comparable top to bottom - the same fix applied after the draft's alignment
// bug (rows on inconsistent bases used to look incoherent next to each other).

// ---------------------------------------------------------------- shared bits
// Our reading of the exhibit above it. Same copy the PDF carries, from
// analysis.section_reads() - one source, so the two surfaces cannot disagree.
function Reads({ bullets }) {
  if (!bullets || !bullets.length) return null;
  return (
    <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
      <div className="tick mb-4">What this tells us</div>
      <ul className="space-y-3">
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-3 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
            <span className="text-[#9a3412] shrink-0">&bull;</span><span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function KeyFigures({ rows, currency }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#ddd8ce] border border-[#ddd8ce]">
      {rows.filter((r) => r.value !== null && r.value !== undefined && r.value !== "").map((r, i) => (
        <div key={i} className="bg-[#f5f2ed] p-5">
          <div className="font-mono-tech text-[15px] text-[#171717] font-medium">{r.value}</div>
          <div className="tick mt-2 text-[#525252]">{r.label}</div>
          {r.note && <div className="mt-2 font-mono-tech text-[10.5px] text-[#666] leading-snug">{r.note}</div>}
        </div>
      ))}
    </div>
  );
}

function DataTable({ head, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full font-mono-tech text-[12px]">
        <thead>
          <tr className="border-b border-[#8f8b85]">
            {head.map((h, i) => (
              <th key={i} className={`py-2.5 pr-4 text-left tick text-[#171717] ${i > 0 ? "whitespace-nowrap" : ""}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#e6e1d8]">
              {r.map((c, j) => (
                <td key={j} className={`py-2.5 pr-4 align-top ${j === 0 ? "text-[#171717]" : "text-[#404040]"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Waterfall({ waterfall }) {
  const rows = waterfall?.rows || [];
  const netSales = waterfall?.checkpoints?.net_sales;
  const cur = waterfall?.currency;
  if (!rows.length) return <p className="font-mono-tech text-[12px] text-[#525252]">Not enough submitted figures to build a waterfall yet.</p>;

  let running = 0;
  const values = rows.map((r) => r.value);
  const totals = [];
  rows.forEach((r) => {
    let start, end;
    if (r.type === "delta") { start = running; end = running + r.value; running = end; }
    else { start = 0; end = r.value; running = r.value; }
    totals.push({ lo: Math.min(start, end), hi: Math.max(start, end) });
  });
  const scaleMin = Math.min(0, ...totals.map((t) => t.lo), ...values.filter((v) => v < 0));
  const scaleMax = Math.max(...totals.map((t) => t.hi), 1);
  const range = scaleMax - scaleMin || 1;
  const pct = (v) => (v / range) * 100;
  const leftOf = (v) => pct(v - scaleMin);
  const zeroLeft = leftOf(0);

  return (
    <div className="flex flex-col" data-testid="audit-waterfall">
      {rows.map((r, i) => {
        const grounded = r.type !== "delta";
        const { lo, hi } = totals[i];
        const left = leftOf(lo);
        const width = Math.max(pct(hi - lo), 0.4);
        const barColor = r.type === "delta"
          ? "repeating-linear-gradient(135deg, #9a3412, #9a3412 4px, transparent 4px, transparent 8px)"
          : r.type === "final" ? (r.value < 0 ? "#9a3412" : "#171717") : "#171717";
        const pctOfNet = netSales ? `${((r.value / netSales) * 100).toFixed(1)}%` : "—";
        return (
          <div
            key={i}
            className={`grid items-center gap-3.5 py-1.5 ${grounded ? "bg-[#eeeae2] rounded px-2.5 my-0.5" : ""}`}
            style={{ gridTemplateColumns: "minmax(140px,220px) 1fr 100px 64px" }}
          >
            <span className={`font-mono-tech text-[11.5px] ${grounded ? "text-[#171717] font-medium" : "text-[#404040]"}`}>
              {r.label}
            </span>
            <div className="h-5 relative">
              <div className="absolute -top-1 -bottom-1 w-px bg-[#c9c3b8]" style={{ left: `${zeroLeft}%` }} />
              <div
                className="absolute top-0 bottom-0 rounded-sm"
                style={{ left: `${left}%`, width: `${width}%`, background: barColor, opacity: r.type === "delta" ? 0.75 : 1 }}
              />
            </div>
            <span className={`font-mono-tech text-[11.5px] text-right tabular-nums ${r.value < 0 ? "text-[#9a3412]" : "text-[#171717]"} ${grounded ? "font-medium" : ""}`}>
              {money(r.value, cur)}
            </span>
            <span className="font-mono-tech text-[10.5px] text-right text-[#525252] tabular-nums">{pctOfNet}</span>
          </div>
        );
      })}
    </div>
  );
}

function ScenarioTester({ reportId, currency }) {
  const [levers, setLevers] = useState(null);
  const [lever, setLever] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getScenarioLevers().then((d) => {
      setLevers(d);
      const first = [...d.revenue_linked, ...d.cost_only][0];
      if (first) setLever(first);
    }).catch(() => setLevers(null));
  }, []);

  const run = async (e) => {
    e.preventDefault();
    if (!lever || value === "") return;
    try {
      setBusy(true);
      setError(null);
      const r = await runAuditScenario(reportId, lever, Number(value));
      setResult(r);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail ? `Could not run that scenario: ${detail}` : "Could not run that scenario. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!levers) return null;

  return (
    <div data-testid="audit-scenario-tester">
      <form onSubmit={run} className="flex flex-wrap items-end gap-4 mb-6">
        <div>
          <label className="tick block mb-2">Change</label>
          <select className="input-vault" value={lever} onChange={(e) => { setLever(e.target.value); setResult(null); }}>
            <optgroup label="Affects revenue too (uses your submitted ROAS)">
              {levers.revenue_linked.map((l) => <option key={l} value={l}>{levers.labels[l]}</option>)}
            </optgroup>
            <optgroup label="Cost only">
              {levers.cost_only.map((l) => <option key={l} value={l}>{levers.labels[l]}</option>)}
            </optgroup>
          </select>
        </div>
        <div>
          <label className="tick block mb-2">To</label>
          <input type="number" step="any" className="input-vault w-40" value={value} onChange={(e) => setValue(e.target.value)} placeholder="New value" />
        </div>
        <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="audit-scenario-run">
          {busy ? "Computing…" : "Run scenario"}
        </button>
      </form>

      {error && <p className="font-mono-tech text-[12px] text-[#9a3412] mb-4">{error}</p>}

      {result && !result.available && (
        <p className="font-mono-tech text-[12px] text-[#9a3412]" data-testid="audit-scenario-unavailable">{result.reason}</p>
      )}

      {result && result.available && (
        <div className="space-y-4" data-testid="audit-scenario-result">
          <div className="flex items-start gap-3 flex-wrap">
            <ModelledBadge />
            <p className="font-mono-tech text-[11.5px] text-[#525252] flex-1 min-w-[200px]">{result.assumption}</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-4">
              <div className="tick mb-1">Net margin — actual</div>
              <div className="font-serif-display text-2xl text-[#171717]">{result.old_checkpoints.net_pct}%</div>
            </div>
            <div className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-4">
              <div className="tick mb-1">Net margin — scenario</div>
              <div className={`font-serif-display text-2xl ${result.new_checkpoints.net_pct >= result.old_checkpoints.net_pct ? "text-[#3f6b4a]" : "text-[#9a3412]"}`}>
                {result.new_checkpoints.net_pct}%
              </div>
            </div>
            <div className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-4">
              <div className="tick mb-1">Net profit — scenario</div>
              <div className="font-serif-display text-2xl text-[#171717]">{money(result.new_checkpoints.net, currency)}</div>
            </div>
          </div>
          <div>
            <div className="tick mb-3">Recomputed waterfall</div>
            <Waterfall waterfall={{ rows: result.new_rows, checkpoints: result.new_checkpoints, currency: result.currency }} />
          </div>
        </div>
      )}
    </div>
  );
}

function Sensitivity({ sensitivity }) {
  const points = sensitivity?.points || [];
  if (!points.length) return null;
  const vals = points.map((p) => p.net_margin_pct).filter((v) => v !== null && v !== undefined);
  const maxAbs = Math.max(1, ...vals.map((v) => Math.abs(v)));
  return (
    <div className="space-y-3" data-testid="audit-sensitivity">
      {points.map((p, i) => {
        const isActual = p.label === "Actual (submitted)";
        const v = p.net_margin_pct ?? 0;
        const width = Math.min(50, (Math.abs(v) / maxAbs) * 50);
        return (
          <div key={i} className={`grid items-center gap-3.5 ${isActual ? "bg-[#eeeae2] rounded px-2.5 py-2" : ""}`}
               style={{ gridTemplateColumns: "150px 1fr 90px" }}>
            <span className="font-mono-tech text-[12px] text-[#171717]">
              {isActual ? <b>{p.paid_share_pct}% paid share (actual)</b> : `${p.paid_share_pct}% paid share`}
            </span>
            <div className="h-4 rounded-full bg-[#dcd7ce] relative overflow-hidden">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#c9c3b8]" />
              {v >= 0 ? (
                <div className="absolute top-0 bottom-0 bg-[#3f6b4a]" style={{ left: "50%", width: `${width}%` }} />
              ) : (
                <div className="absolute top-0 bottom-0 bg-[#9a3412]" style={{ right: "50%", width: `${width}%` }} />
              )}
            </div>
            <span className={`font-mono-tech text-[12px] text-right tabular-nums ${v < 0 ? "text-[#9a3412]" : "text-[#3f6b4a]"}`}>
              {p.net_margin_pct != null ? `${v > 0 ? "+" : ""}${v}%` : "—"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Plain-English walkthrough of what the sensitivity bars actually mean -
// computed client-side from numbers the report already carries (breakeven
// share, the submitted actual, the stress point), not a separate model call.
function SensitivityCommentary({ sensitivity }) {
  const be = sensitivity?.breakeven_paid_share_pct;
  const points = sensitivity?.points || [];
  const actual = points.find((p) => p.label === "Actual (submitted)");
  const stress = points.find((p) => p.label === "+15pt stress");
  if (be == null || !actual) return null;
  const gap = +(actual.paid_share_pct - be).toFixed(1);
  const direction = gap > 0 ? "above" : "below";
  return (
    <p className="font-mono-tech text-[12.5px] text-[#404040] leading-relaxed mb-5">
      This models net margin at different splits between paid-acquired revenue and everything else
      (organic, referral, repeat), holding blended ROAS ({sensitivity.roas}) and every other
      waterfall line fixed. <b className="text-[#171717]">{be}% paid share is the modelled
      breakeven</b> — below it, marketing spend is more than covered by what it brings in; above
      it, spend outpaces what the business can fund. The submitted actual sits at{" "}
      {actual.paid_share_pct}%, {Math.abs(gap)} points {direction} that line, putting net margin at{" "}
      {actual.net_margin_pct}%.{" "}
      {stress && (
        <>Pushing another 15 points further to {stress.paid_share_pct}% would deepen that to{" "}
        {stress.net_margin_pct}% — the trend gets worse the more the business leans on paid, not better.</>
      )}
    </p>
  );
}

function benchVal(v) {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const nums = v.replace(/%/g, "").split("-").map((s) => parseFloat(s.trim())).filter((n) => !isNaN(n));
    if (nums.length) return nums.reduce((a, b) => a + b, 0) / nums.length;
  }
  return null;
}

function BenchmarkBar({ row }) {
  const b = row.benchmark;
  const benchV = benchVal(row.benchmark_value);
  if (benchV == null) return null;
  const max = Math.max(row.actual_value, benchV, 1) * 1.2;
  const beats = row.higher_is_better ? row.actual_value >= benchV : row.actual_value <= benchV;
  let host = null;
  try { host = new URL(b.url).hostname; } catch { /* no-op */ }
  return (
    <div className="mb-6 last:mb-0" data-testid={`benchmark-bar-${row.metric}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="tick">{row.label}</span>
        <span className={`tick ${beats ? "text-[#3f6b4a]" : "text-[#9a3412]"}`}>{beats ? "Beats benchmark" : "Trails benchmark"}</span>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-3">
          <span className="font-mono-tech text-[10px] text-[#525252] w-20 shrink-0">You</span>
          <div className="flex-1 h-3 bg-[#dcd7ce] rounded-full overflow-hidden">
            <div className="h-full bg-[#171717] rounded-full" style={{ width: `${Math.min(100, (row.actual_value / max) * 100)}%` }} />
          </div>
          <span className="font-mono-tech text-[11px] text-[#171717] w-16 text-right shrink-0">{row.actual_value}%</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono-tech text-[10px] text-[#525252] w-20 shrink-0">Benchmark</span>
          <div className="flex-1 h-3 bg-[#dcd7ce] rounded-full overflow-hidden">
            <div className="h-full bg-[#e88a51] rounded-full" style={{ width: `${Math.min(100, (benchV / max) * 100)}%` }} />
          </div>
          <span className="font-mono-tech text-[11px] text-[#171717] w-16 text-right shrink-0">
            {typeof row.benchmark_value === "string" ? row.benchmark_value : `${row.benchmark_value}%`}
          </span>
        </div>
      </div>
      <div className="mt-2 font-mono-tech text-[10px] text-[#525252]">
        {b.fallback ? "General online-retail benchmark (no category-specific figure available)" : b.subtype || b.category}
        {" — "}{b.source}
        {host && <> — <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-[#9a3412] underline decoration-dotted">{host}</a></>}
      </div>
    </div>
  );
}

export default function AuditReportView({ report, embedded = false }) {
  const {
    business_name, store_domain, verdict, executive_summary, cross_reference_insight,
    waterfall = {}, findings = [], sensitivity = {}, reconciliation = {},
    benchmark_comparisons = [], competitor_domains = [], operator_note, decisions_for_call = [],
    // Everything below is computed server-side and rendered in the PDF. The web
    // view is a SUPERSET of the PDF, never a different set - same sections, same
    // order, same figures, plus the interactivity a document cannot have.
    // tests/test_report_parity.py fails if a block the PDF renders is missing here.
    headline = {}, section_claims = {}, section_reads = {}, marketing_efficiency = {},
    binding_constraint = {}, customer_economics = {}, scenarios = {}, target_state = {},
    market_position = {}, own_catalog = {}, shipping_model = {},
    their_question, answer_to_their_question, evidence_coverage = {},
    // Evidence-pack analysis. Each mirrors a PDF section; tests/test_report_parity
    // fails if one renders in the document and never reaches this surface.
    order_economics = {}, marketing_evidence = {}, pricing_evidence = {},
    retention_evidence = {}, promo_calendar = {}, evidence_reconciliation = {},
    brand_positioning,
  } = report;
  const covLines = evidence_coverage.lines || [];
  const covStatusClass = (s) =>
    s === "decision_grade" || s === "available"
      ? "text-[#3f6b4a] bg-[rgba(63,107,74,0.1)]"
      : s === "sensitivity_only" || s === "limited"
      ? "text-[#8a5a3a] bg-[rgba(138,90,58,0.1)]"
      : "text-[#9a3412] bg-[rgba(154,52,18,0.1)]";
  const cp = waterfall.checkpoints || {};
  const gaps = waterfall.gaps || [];

  const own = reconciliation.own || {};
  const hyp = reconciliation.hypothetical;
  const hasOwn = Object.keys(own).length > 0;

  return (
    <div className="space-y-10" data-testid="audit-report-view">
      {!embedded && (
      <div className="card-cream flex items-center justify-between gap-3 p-4" data-testid="audit-report-toolbar">
        <Link to="/account" className="tick hover:text-[#171717] flex items-center gap-2 transition-colors" data-testid="audit-back-link">
          <ArrowLeft size={12} /> Back to my account
        </Link>
        <span className="tick">{business_name} · {store_domain}</span>
      </div>
      )}

      {/* The answer page. Same opening as the PDF, and the same sentence: one
          computed verdict, the figures that support it, then the reasons.
          Previously the web view opened with "The margin call" and "Executive
          summary" back to back - two model paragraphs saying overlapping things,
          which is the duplication the PDF rebuild removed and this one did not.
          tests/test_report_parity.py now fails if the two surfaces open on
          different verdicts. */}
      <section className="card-raised p-8 md:p-10" data-testid="audit-report-answer">
        <div className="tick mb-6">Margin audit &middot; the answer</div>
        <h2 className="font-serif-display text-3xl md:text-[40px] text-[#171717] leading-[1.15] max-w-4xl">
          {section_claims.answer || "What this audit found"}
        </h2>

        <div className="mt-8 grid sm:grid-cols-3 gap-px bg-[#ddd8ce] border border-[#ddd8ce]">
          {[
            { v: money(headline.net_monthly, waterfall.currency),
              l: `Net profit today, per month (${headline.net_pct}% of net sales)` },
            target_state.improvement
              ? { v: `${money(target_state.improvement, waterfall.currency)} / mo`,
                  l: "Added to net profit if the full plan is delivered" }
              : { v: `${money(headline.identified_monthly, waterfall.currency)} / mo`,
                  l: "Added to net profit by the levers we sized" },
            target_state.closing_net != null
              ? { v: `${money(target_state.closing_net, waterfall.currency)} / mo`,
                  l: `Net profit after the plan (${target_state.closing_net_pct}% of net sales)` }
              : { v: money(headline.identified_annual, waterfall.currency), l: "The same, annualised" },
          ].map((t, i) => (
            <div key={i} className="bg-[#f5f2ed] p-5">
              <div className="font-mono-tech text-[15px] text-[#171717] font-medium">{t.v}</div>
              <div className="tick mt-2 text-[#525252]">{t.l}</div>
            </div>
          ))}
        </div>

        {(() => {
          const why = [];
          const con = binding_constraint.constraint_excluding_acquisition || binding_constraint.constraint;
          if (con) why.push(<><strong>{con.name} is the line out of shape.</strong> It runs at {pctOfBase(con)} against roughly {con.tolerance_pct}% for a business of this size and channel — {money(con.at_stake_monthly, waterfall.currency)} a month sits above that line.</>);
          if (marketing_efficiency.marketing_is_accretive === false) why.push(<><strong>Paid acquisition is bought below its own break-even.</strong> One dollar returns {marketing_efficiency.contribution_per_marketing_dollar} of contribution; it needs a blended return of {marketing_efficiency.breakeven_roas}x to wash its face and runs at {marketing_efficiency.roas}x.</>);
          if (cp.cm1_pct != null) why.push(<><strong>The product margin is not the problem.</strong> Gross margin holds at {cp.cm1_pct}% and contribution survives to {cp.cm3_pct}% after customer acquisition. Every dollar of the loss appears below that line.</>);
          const best = (scenarios.scenarios || [])[0];
          if (best) why.push(<><strong>The strongest single move is {String(best.label).toLowerCase()}</strong>, which adds {money(best.delta_vs_today, waterfall.currency)} a month to net profit{best.breakeven_volume_loss_pct != null ? ` provided unit volume falls by less than ${best.breakeven_volume_loss_pct}%` : ""}.</>);
          if (target_state.reaches_breakeven === false) why.push(<><strong>Doing all of it does not fix the business.</strong> Every recommended move together lands at {money(target_state.closing_net, waterfall.currency)} ({target_state.closing_net_pct}% of net sales). That is the honest ceiling of a margin plan here.</>);
          if (!why.length) return null;
          return (
            <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
              <div className="tick mb-4">Why we conclude that</div>
              <ol className="space-y-4">
                {why.map((w, i) => (
                  <li key={i} className="flex gap-4 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
                    <span className="text-[#171717] font-medium shrink-0">{i + 1}</span><span>{w}</span>
                  </li>
                ))}
              </ol>
            </div>
          );
        })()}

        {their_question && (
        <Section
          testid="audit-question-section"
          tag="The question you asked"
          title="Answered first, before anything we found on our own"
        >
          <blockquote className="border-l-2 border-[#e88a51] bg-[#efece5] p-6 font-serif-display text-xl text-[#171717] leading-relaxed">
            {their_question}
          </blockquote>
          {!!(answer_to_their_question && answer_to_their_question.length) && (
            <div className="mt-6">
              <div className="tick mb-4">Our answer</div>
              <ul className="space-y-3">
                {(Array.isArray(answer_to_their_question) ? answer_to_their_question : [answer_to_their_question])
                  .filter(Boolean).map((pt, i) => (
                  <li key={i} className="flex gap-3 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
                    <span className="text-[#9a3412] shrink-0">&bull;</span><span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>
      )}

      {!!covLines.length && (
        <Section
          testid="audit-evidence-coverage-section"
          tag="Evidence coverage"
          title="What this audit is entitled to conclude"
          subtitle="Each decision is only as strong as the evidence behind it. Where a line is not decision-grade, the recommendation is withheld and the smallest test that would unlock it is named — no gap is filled with an assumption."
        >
          <div className="divide-y divide-[#ddd8ce]">
            {covLines.map((ln, i) => (
              <div key={i} className="py-4 grid md:grid-cols-[220px_120px_1fr] gap-3 md:gap-6 items-start" data-testid={`evidence-line-${ln.key}`}>
                <div className="font-mono-tech text-[13px] text-[#171717] font-medium">{ln.label}</div>
                <div>
                  <span className={`tick px-2.5 py-1 rounded-full whitespace-nowrap ${covStatusClass(ln.status)}`}>
                    {ln.status_label || ln.status}
                  </span>
                </div>
                <div className="font-mono-tech text-[12px] text-[#404040] leading-relaxed">
                  {ln.why}
                  {ln.validation_test && (
                    <div className="mt-2 text-[#9a3412]">
                      <span className="tick">Smallest test</span> — {ln.validation_test}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {!!(evidence_reconciliation.checks || []).length && (
        <Section
          testid="audit-reconciliation-section"
          tag="Cross-checks"
          title="What we checked your figures against"
          subtitle="Every export you supplied is reconciled against the P&amp;L you submitted before anything is built on it. These are your own numbers, checked against each other."
        >
          <div className="divide-y divide-[#ddd8ce]">
            {evidence_reconciliation.checks.map((c, i) => (
              <div key={i} className="py-3 grid md:grid-cols-[240px_110px_1fr] gap-3 md:gap-6 items-start">
                <div className="font-mono-tech text-[13px] text-[#171717]">
                  {String(c.check || "").replace(/_/g, " ").replace(/^./, (s) => s.toUpperCase())}
                </div>
                <div className={`tick ${c.status === "ok" ? "text-[#3f6b4a]" : "text-[#9a3412]"}`}>
                  {c.status === "ok" ? "agrees" : c.status === "warn" ? "disagrees" : "not checked"}
                </div>
                <div className="font-mono-tech text-[12px] text-[#404040] leading-relaxed">{c.detail}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {!!(order_economics.rows || []).length && (
        <Section
          testid="audit-order-economics-section"
          tag="What one order actually earns"
          title={section_claims.unit || `One order, from ${money(order_economics.revenue_per_order, order_economics.currency)} in to ${money(order_economics.net_per_order, order_economics.currency)} at the bottom`}
          subtitle="Every line of the waterfall above, divided by the orders that produced it. Same figures, same single calculation - this is the monthly picture in the unit the decisions are actually made in."
        >
          {order_economics.aov_reconciliation && (
            <p className="mb-5 font-mono-tech text-[12px] text-[#9a3412] leading-relaxed">
              {order_economics.aov_reconciliation}
            </p>
          )}
          <div className="divide-y divide-[#ddd8ce]">
            {order_economics.rows.map((r, i) => (
              <div key={i} className="py-2.5 flex justify-between gap-6">
                <span className="font-mono-tech text-[13px] text-[#171717]">{r.label}</span>
                <span className="font-mono-tech text-[13px] text-[#171717] tabular-nums">
                  {money(r.per_order, order_economics.currency)}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {!!(marketing_evidence.campaigns || []).length && (
        <Section
          testid="audit-marketing-evidence-section"
          tag="What your ad spend actually bought"
          title={section_claims.ad_spend || "Your campaigns, against the return they have to clear"}
          subtitle="Summed from the campaign export you supplied. Every return here is the one the platform reports, which is the right basis for ranking your campaigns against each other and the wrong basis for claiming what any of them added."
        >
          {!!marketing_evidence.dead_spend && (
            <p className="mb-3 font-mono-tech text-[13px] text-[#171717] leading-relaxed">
              <strong>{money(marketing_evidence.dead_spend, waterfall.currency)}</strong>{" "}
              ({marketing_evidence.dead_spend_share_pct}% of the spend bought to convert) sits in
              campaigns that were bought to produce sales and produced none:{" "}
              {(marketing_evidence.dead_campaigns || []).map((d) => d.name).join(", ")}.
            </p>
          )}
          {!!marketing_evidence.upper_funnel && (
            <p className="mb-3 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
              <strong>{money(marketing_evidence.upper_funnel.spend, waterfall.currency)}</strong>{" "}
              ({marketing_evidence.upper_funnel.share_pct}%) is awareness, reach or traffic spend.{" "}
              {marketing_evidence.upper_funnel.note}
            </p>
          )}
          {marketing_evidence.attribution_note && (
            <p className="mb-4 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
              {marketing_evidence.attribution_note}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full font-mono-tech text-[12px]">
              <thead>
                <tr className="border-b border-[#171717] text-left text-[#525252]">
                  <th className="py-2 pr-4 font-normal">Campaign</th>
                  <th className="py-2 pr-4 font-normal">Spend</th>
                  <th className="py-2 pr-4 font-normal">Platform ROAS</th>
                  <th className="py-2 pr-4 font-normal">Purchases</th>
                  <th className="py-2 font-normal">vs break-even</th>
                </tr>
              </thead>
              <tbody>
                {marketing_evidence.campaigns.map((c, i) => (
                  <tr key={i} className="border-b border-[#ddd8ce]">
                    <td className="py-2 pr-4 text-[#171717]">{c.name}{c.level === "ad set" ? " (ad set)" : ""}</td>
                    <td className="py-2 pr-4 tabular-nums">{money(c.spend, waterfall.currency)}</td>
                    <td className="py-2 pr-4 tabular-nums">{c.platform_roas != null ? `${c.platform_roas}x` : "-"}</td>
                    <td className="py-2 pr-4 tabular-nums">{Math.round(c.purchases || 0)}</td>
                    <td className="py-2 text-[#404040]">
                      {c.intent === "upper_funnel"
                        ? "not judged - upper funnel"
                        : c.intent === "unknown"
                        ? "objective not stated"
                        : marketing_evidence.breakeven_roas && c.platform_roas != null
                        ? c.platform_roas < marketing_evidence.breakeven_roas ? "below" : "clears"
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {!!(pricing_evidence.skus || []).length && (
        <Section
          testid="audit-pricing-evidence-section"
          tag="Where the revenue actually is"
          title={section_claims.revenue_shape || "What your range earns, SKU by SKU"}
          subtitle="Summed from the weekly sales export you supplied. The catalogue read can see what a range looks like; only this can see what it earns."
        >
          {!!pricing_evidence.top5_share_pct && (
            <p className="mb-3 font-mono-tech text-[13px] text-[#171717] leading-relaxed">
              Your five largest SKUs carry <strong>{pricing_evidence.top5_share_pct}%</strong> of revenue in this panel.
            </p>
          )}
          {pricing_evidence.discount_note && (
            <p className="mb-4 font-mono-tech text-[12px] text-[#404040] leading-relaxed">{pricing_evidence.discount_note}</p>
          )}
          {!!pricing_evidence.pocket_price && (
            <div className="mb-6 divide-y divide-[#ddd8ce]">
              {[
                ["List price per unit", pricing_evidence.pocket_price.list_per_unit],
                ["Less discount", -(pricing_evidence.pocket_price.discount_per_unit || 0)],
                ["Less refunds", -(pricing_evidence.pocket_price.refund_per_unit || 0)],
                ["Pocket price", pricing_evidence.pocket_price.realised_per_unit],
              ].map(([label, v], i) => (
                <div key={i} className="py-2.5 flex justify-between gap-6">
                  <span className="font-mono-tech text-[13px] text-[#171717]">{label}</span>
                  <span className="font-mono-tech text-[13px] text-[#171717] tabular-nums">
                    {money(v, pricing_evidence.currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {!!((pricing_evidence.cost_coverage || {}).loss_making || []).length && (
            <>
              <div className="tick mb-2">Sold below their own unit cost</div>
              <div className="divide-y divide-[#ddd8ce] mb-6">
                {pricing_evidence.cost_coverage.loss_making.slice(0, 12).map((x, i) => (
                  <div key={i} className="py-2.5 flex justify-between gap-6">
                    <span className="font-mono-tech text-[13px] text-[#171717]">{x.name}</span>
                    <span className="font-mono-tech text-[13px] text-[#9a3412] tabular-nums">
                      {money(x.unit_margin, pricing_evidence.currency)} / unit
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
          {!!(pricing_evidence.tail || {}).sku_count && (
            <p className="font-mono-tech text-[12px] text-[#404040] leading-relaxed">
              <strong className="text-[#171717]">{pricing_evidence.tail.caveat}</strong>
            </p>
          )}
        </Section>
      )}

      {!!(retention_evidence.repayment_curve || []).length && (
        <Section
          testid="audit-retention-evidence-section"
          tag="When acquisition gets paid back"
          title={section_claims.repayment || "How long a customer takes to repay what they cost"}
          subtitle="Measured from the cohort file you supplied. Orders per customer here is counted, not derived from a repeat rate - which is what makes the ratio below a measurement rather than an identity."
        >
          {retention_evidence.read && (
            <p className="mb-5 font-mono-tech text-[13px] text-[#171717] leading-relaxed">{retention_evidence.read}</p>
          )}
          <div className="divide-y divide-[#ddd8ce]">
            {retention_evidence.repayment_curve.map((r, i) => (
              <div key={i} className="py-2.5 grid grid-cols-[1fr_auto_auto] gap-6">
                <span className="font-mono-tech text-[13px] text-[#171717]">Within {r.days} days</span>
                <span className="font-mono-tech text-[12px] text-[#404040] tabular-nums">
                  {r.orders_per_customer} orders / customer
                </span>
                <span className="font-mono-tech text-[13px] text-[#171717] tabular-nums">
                  {r.share_of_cac_repaid_pct != null ? `${r.share_of_cac_repaid_pct}% repaid` : "-"}
                </span>
              </div>
            ))}
          </div>
          {retention_evidence.ltv_to_cac != null && (
            <p className="mt-5 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
              Over the whole file a customer places {retention_evidence.orders_per_customer_measured} orders,
              a ratio of <strong className="text-[#171717]">{retention_evidence.ltv_to_cac}</strong> against
              acquisition. {retention_evidence.ltv_basis}
            </p>
          )}
        </Section>
      )}

      {!!(promo_calendar.months || []).length && promo_calendar.read && (
        <Section
          testid="audit-promo-calendar-section"
          tag="List price versus promotion"
          title="How often you reposition, and how often you discount"
          subtitle="Counted from your own price and promotion history, in place of the catalogue read's inference from update timestamps."
        >
          <p className="font-mono-tech text-[13px] text-[#171717] leading-relaxed">{promo_calendar.read}</p>
        </Section>
      )}

      <Section
        testid="audit-waterfall-section"
        tag="The waterfall"
        title={`Where ${money(cp.gross_sales, waterfall.currency)} of gross sales actually went`}
        subtitle="Every bar sits on one shared scale, floating from where the running total stood to where it stood after that line - bar position and length are directly comparable top to bottom. Solid bars are checkpoints; hatched bars are what got cut between them. Percentages are of net sales."
      >
        <Waterfall waterfall={waterfall} />
        <Reads bullets={section_reads.waterfall} />
      </Section>

      {!!marketing_efficiency.breakeven_roas && (
        <Section
          testid="audit-acquisition-section"
          tag="What it costs you to win a customer"
          title={section_claims.efficiency || "What a marketing dollar returns"}
          subtitle="Computed from your own contribution rate and your own submitted return on ad spend. No category average is used, and none is needed."
        >
          <KeyFigures currency={waterfall.currency} rows={[
            { value: `${marketing_efficiency.contribution_per_marketing_dollar}`, label: "Contribution returned per dollar of paid acquisition", note: marketing_efficiency.formula },
            { value: `${marketing_efficiency.value_per_dollar_cents} cents`, label: "Value created per dollar spent" },
            { value: `${marketing_efficiency.breakeven_roas}x`, label: "Blended ROAS needed to break even", note: `You run at ${marketing_efficiency.roas}x` },
          ]} />
          {marketing_efficiency.breakeven_reachable_on_marketing_alone === false && (
            <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
              <div className="tick mb-4">The boundary: what happens at zero marketing</div>
              <DataTable
                head={["Step", "Amount", "Basis"]}
                rows={[
                  ["Gross sales not attributed to paid", money(marketing_efficiency.non_paid_gross, waterfall.currency), "Scenario: gross sales x (1 - paid share)"],
                  ["Contribution that base produces", money(marketing_efficiency.non_paid_contribution, waterfall.currency), "Scenario: holds every variable cost rate constant"],
                  ["Less fixed overhead", money(-(marketing_efficiency.fixed_overhead || 0), waterfall.currency), "Measured: submitted figure"],
                  ["Net profit at zero marketing", money(marketing_efficiency.net_at_zero_marketing, waterfall.currency), "Still loss-making"],
                ]}
              />
              <p className="mt-4 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
                Fixed overhead exceeds the contribution the non-paid base generates by{" "}
                <strong>{money(marketing_efficiency.overhead_uncovered_at_zero_marketing, waterfall.currency)}</strong> a month.
                No marketing decision, however well executed, closes that.
              </p>
            </div>
          )}
          {!!customer_economics.cac && (
            <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
              <div className="tick mb-4">Over a customer&apos;s life, not one order</div>
              <DataTable
                head={["Measure", "Value", "How it is built"]}
                rows={[
                  ["Cost to acquire a customer", money(customer_economics.cac, waterfall.currency), "Acquisition spend / new customers"],
                  ["Contribution from one order", money(customer_economics.contribution_per_order, waterfall.currency), "AOV x net/gross x contribution rate"],
                  ["Orders needed to repay it", `${customer_economics.orders_to_repay_cac}`, "Below 1.0 means the first order pays for itself"],
                  ["Orders per customer, 12 months", `${customer_economics.orders_per_customer_12m ?? "-"}`, customer_economics.orders_per_customer_derived ? "Derived from your repeat rate" : "Submitted"],
                ].concat(customer_economics.ltv_to_cac ? [["Lifetime value to acquisition cost", `${customer_economics.ltv_to_cac}:1`, "Contribution over a year divided by what it cost to acquire"]] : [])}
              />
              {customer_economics.ltv_to_cac_withheld && (
                <p className="mt-4 font-mono-tech text-[11px] text-[#666] leading-relaxed">
                  <strong>Lifetime value to acquisition cost is not shown.</strong> {customer_economics.ltv_to_cac_withheld}
                </p>
              )}
              {customer_economics.read && (
                <p className="mt-4 font-mono-tech text-[12px] text-[#171717] leading-relaxed">{customer_economics.read}</p>
              )}
            </div>
          )}
        </Section>
      )}

      {!!(binding_constraint.lines && binding_constraint.lines.length) && (
        <Section
          testid="audit-constraint-section"
          tag={`Your binding constraint: ${(section_claims.constraint_name || "cost structure").toLowerCase()}`}
          title={section_claims.constraint || "Which cost line is holding the business back"}
          subtitle="Every cost line scored against what a business of this size and channel can carry, measured in dollars at stake rather than percentage points."
        >
          <DataTable
            head={["Cost line", "% of sales", "Working band", "Points over", "Dollars above the line"]}
            rows={binding_constraint.lines.map((l) => [
              l.name, pctCell(l), `${l.tolerance_pct}%`,
              l.over_tolerance ? `+${l.excess_points}` : "within",
              l.over_tolerance ? money(l.at_stake_monthly, waterfall.currency) : "-",
            ])}
          />
          {binding_constraint.working_band_implied_margin_pct != null && (
            <p className="mt-3 text-[12px] text-[#525252]" data-testid="working-band-note">
              {workingBandNote(binding_constraint.working_band_implied_margin_pct)}
            </p>
          )}
          {binding_constraint.lines.some((l) => l.base === "gross_sales") && (
            <p className="mt-3 text-[12px] text-[#525252]" data-testid="cost-line-base-note">{BASE_NOTE}</p>
          )}
          {!!(binding_constraint.constraint_breakdown && binding_constraint.constraint_breakdown.parts) && (
            <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
              <div className="tick mb-4">What it consists of</div>
              <DataTable
                head={["Component", "Per month", "Share of the line", "% of net sales"]}
                rows={binding_constraint.constraint_breakdown.parts.map((pt) => [
                  pt.label, money(pt.amount, waterfall.currency), `${pt.share_of_line_pct}%`, `${pt.pct_of_net_sales}%`,
                ])}
              />
            </div>
          )}
          {!!(binding_constraint.fixed_base && binding_constraint.fixed_base.routes) && (
            <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
              <div className="tick mb-4">What the fixed base demands in revenue</div>
              <p className="mb-5 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
                One part of this is not an assumption: the fixed base has to be covered by contribution, whatever
                revenue produces it. That means{" "}
                <strong>{money(binding_constraint.fixed_base.contribution_required, waterfall.currency)}</strong> a month
                against the {money(binding_constraint.fixed_base.contribution_today, waterfall.currency)} being produced
                now. What it means in revenue depends on what happens to your cost rates as volume grows, so it is shown
                as a band.
              </p>
              <DataTable
                head={["Rate path", "Contribution rate after acquisition", "Fixed base at that scale", "Net sales needed", "Multiple of today"]}
                rows={binding_constraint.fixed_base.routes.map((r) => [
                  r.label, `${r.contribution_rate_pct}%`, money(r.fixed_costs_at_scale, waterfall.currency),
                  r.net_sales_needed ? money(r.net_sales_needed, waterfall.currency) : "unreachable",
                  r.multiple_of_today ? `${r.multiple_of_today}x` : "-",
                ])}
              />
            </div>
          )}
        </Section>
      )}

      {findings.length > 0 && (
        <Section
          testid="audit-findings-register"
          tag="Findings register"
          title="What the waterfall proves, ranked"
          subtitle="Confidence here reflects internal consistency of the figures you provided, not verification against bank statements or ad-platform exports - this audit is built entirely from your intake submission, by design."
        >
          <div className="space-y-4">
            {findings.map((f, i) => (
              <div key={f.code || i} className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-5" data-testid={`audit-finding-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="tick text-[#9a3412]">{f.code}</span>
                    <h4 className="font-serif-display text-lg text-[#171717]">{f.title}</h4>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`tick uppercase ${sevColor(f.severity)}`}>{f.severity} severity</span>
                    <span className="tick text-[#525252]">{f.confidence} confidence</span>
                  </div>
                </div>
                <p className="font-mono-tech text-[13px] text-[#404040] leading-relaxed mb-2">{f.observation}</p>
                <p className="font-mono-tech text-[12px] text-[#525252] leading-relaxed mb-3">{f.implication}</p>
                <div className="pt-3 border-t border-[#e8e4df] flex items-start gap-2">
                  <span className="tick text-[#171717] shrink-0">Do this →</span>
                  <p className="font-mono-tech text-[12px] text-[#171717] leading-relaxed">{f.action}</p>
                </div>
                {f.benchmark && (
                  <div className="mt-3 pt-3 border-t border-dashed border-[#c9c3b8] font-mono-tech text-[11.5px] text-[#404040]">
                    <b className="text-[#171717]">Benchmark:</b> {f.benchmark.value}% — {f.benchmark.label}.{" "}
                    <i>Source: {f.benchmark.source}.</i>{" "}
                    <a href={f.benchmark.url} target="_blank" rel="noopener noreferrer" className="text-[#9a3412] underline decoration-dotted">
                      {new URL(f.benchmark.url).hostname}
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {hasOwn && (
        <Section
          testid="audit-cross-reference"
          tag="Cross-referenced"
          title="What your free sweep already told us, now quantified"
          subtitle="This is the value of buying both: the free sweep measured your own storefront from public data; this audit puts a real cost behind it using your actual cost structure."
        >
          <div className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-5 space-y-3">
            <div className="tick">From your own storefront's public catalog</div>
            <div className="flex justify-between border-b border-[#e8e4df] py-2 font-mono-tech text-[12px]">
              <span className="text-[#404040]">Your on-sale share (measured)</span>
              <span className="text-[#171717] font-medium">{own.on_sale_breadth_pct != null ? `${own.on_sale_breadth_pct}%` : "data_unavailable"}</span>
            </div>
            <div className="flex justify-between border-b border-[#e8e4df] py-2 font-mono-tech text-[12px]">
              <span className="text-[#404040]">Your average discount depth (measured)</span>
              <span className="text-[#171717] font-medium">{own.avg_discount_depth_pct != null ? `${own.avg_discount_depth_pct}%` : "data_unavailable"}</span>
            </div>
            <div className="flex justify-between py-2 font-mono-tech text-[12px]">
              <span className="text-[#404040]">Your stockout rate (measured)</span>
              <span className="text-[#171717] font-medium">{own.sold_out_pct != null ? `${own.sold_out_pct}%` : "data_unavailable"}</span>
            </div>
            {cross_reference_insight && (
              <p className="mt-2 font-mono-tech text-[12.5px] text-[#404040] leading-relaxed">{cross_reference_insight}</p>
            )}
            {hyp && (
              <div className="mt-2 border-t border-[#e8e4df] pt-3">
                <div className="mb-2"><ModelledBadge children="Modelled scenario" /></div>
                <p className="font-mono-tech text-[12.5px] text-[#404040] leading-relaxed">
                  Your added competitor <b className="text-[#171717]">{hyp.reference_domain}</b> carries a measured on-sale
                  share of {hyp.reference_breadth_pct}% at {hyp.reference_depth_pct}% average depth. Matching even half that
                  breadth ({hyp.assumed_breadth_pct}% of your catalog, at their measured depth) on your own catalog would cost
                  roughly <b className="text-[#171717]">{money(hyp.modelled_monthly_cost, waterfall.currency)}/month</b>
                  {hyp.pct_of_cm3 != null && <> — {hyp.pct_of_cm3}% of your entire CM3 contribution margin</>}.
                </p>
              </div>
            )}
          </div>
        </Section>
      )}

      {sensitivity?.points?.length > 0 && (
        <Section
          testid="audit-sensitivity-section"
          tag="Sensitivity"
          title="Net margin vs. paid-attributed revenue share"
          badge={<ModelledBadge />}
          subtitle="This one relationship - net margin against how much revenue comes from paid acquisition - is fixed here because marketing spend and revenue are linked through ROAS. To test any other input, use 'Test a scenario' below."
        >
          <SensitivityCommentary sensitivity={sensitivity} />
          <Sensitivity sensitivity={sensitivity} />
        </Section>
      )}

      <Section
        testid="audit-scenario-section"
        tag="Test a scenario"
        title="What if one number were different?"
        badge={<ModelledBadge />}
        subtitle="Pick one input and see the whole waterfall recompute - marketing spend and blended ROAS also model the revenue that spend buys, not just the cost saved. Everything else stays exactly as submitted."
      >
        <ScenarioTester reportId={report.id} currency={waterfall.currency} />
      </Section>

      {benchmark_comparisons.length > 0 && (
        <Section
          testid="audit-benchmarks"
          tag="Benchmark context"
          title="How you compare against the category"
          subtitle="Each row is your measured result next to a cited external benchmark for your category - real context for whether the waterfall above is normal or an outlier for a business like yours, not a verdict on its own."
        >
          <div className="grid sm:grid-cols-2 gap-x-8">
            {benchmark_comparisons.map((row) => <BenchmarkBar key={row.metric} row={row} />)}
          </div>
        </Section>
      )}

        {!!(market_position.dimensions && market_position.dimensions.length) && (
        <Section
          testid="audit-position-section"
          tag="Where you sit in the market"
          title={section_claims.position || "Your position, measured rather than assumed"}
          subtitle="Live catalogues read on the same day."
        >
          {brand_positioning && (
            <div className="mb-6">
              <div className="tick mb-2">How you describe the brand</div>
              <blockquote className="border-l-2 border-[#e88a51] bg-[#efece5] p-5 font-mono-tech text-[13px] text-[#171717] leading-relaxed">
                {brand_positioning}
              </blockquote>
              <p className="mt-2 font-mono-tech text-[11px] text-[#525252] leading-relaxed">
                Your words, as submitted. The measurements below are what your published prices say
                alongside them. Where the two disagree, the question is which one you intend to change.
              </p>
            </div>
          )}
          {market_position.price_index_read && (
            <p className="mb-6 font-mono-tech text-[12px] text-[#171717] leading-relaxed">{market_position.price_index_read}</p>
          )}
          <DataTable
            head={["Dimension", "You", "Them", "The read"]}
            rows={market_position.dimensions.map((d) => [d.name, `${d.ours}`, `${d.theirs}`, d.read])}
          />
          {!!(market_position.white_space && market_position.white_space.length) && (
            <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
              <div className="tick mb-4">Price bands they hold and you do not</div>
              <DataTable
                head={["Price band", "Their share of range", "Yours"]}
                rows={market_position.white_space.map((g) => [g.band, `${g.their_share_pct}%`, `${g.your_share_pct}%`])}
              />
            </div>
          )}
          <Reads bullets={section_reads.position} />
        </Section>
      )}

      {!!(scenarios.scenarios && scenarios.scenarios.length) && (
        <Section
          testid="audit-scenarios-section"
          tag="What happens if you move"
          title={section_claims.scenarios || "Every lever, modelled"}
          subtitle="Each row is one lever pulled on its own and re-run through the same waterfall as the baseline. They are alternatives, not additions."
          badge={<ModelledBadge>Scenario, not a forecast</ModelledBadge>}
        >
          <DataTable
            head={["Move", "Net profit effect / mo", "Net profit lands at", "Unit volume it can afford to lose", "Breaks even?"]}
            rows={scenarios.scenarios.map((x) => [
              x.label, money(x.delta_vs_today, waterfall.currency),
              `${money(x.net, waterfall.currency)} (${x.net_pct}%)`,
              x.breakeven_volume_loss_pct != null ? `${x.breakeven_volume_loss_pct}%` : "not volume-linked",
              x.reaches_breakeven ? "yes" : "no",
            ])}
          />
          <p className="mt-5 font-mono-tech text-[12px] text-[#171717] leading-relaxed">
            <strong>Read the fourth column, not the second.</strong> {scenarios.volume_caveat}
          </p>
          {scenarios.acquisition_decision_rule && (
            <p className="mt-4 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
              {scenarios.acquisition_decision_rule.rule}
            </p>
          )}
          <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
            <div className="tick mb-4">What each move assumes</div>
            <DataTable head={["Move", "The assumption that has to hold"]}
                       rows={scenarios.scenarios.map((x) => [x.label, x.assumption])} />
          </div>
          <Reads bullets={section_reads.scenarios} />
        </Section>
      )}

      {!!(target_state.waterfall) && (
        <Section
          testid="audit-target-section"
          tag="Where the plan lands you"
          title={section_claims.target_state || "The business if every move is made"}
          subtitle="The same waterfall, re-run with every recommended move applied at once. This is the ceiling of the plan, not a forecast."
          badge={<ModelledBadge>Scenario, not a forecast</ModelledBadge>}
        >
          <DataTable
            head={["Line", "Today", "After the plan", "Change"]}
            rows={[["Net sales", "net_sales"], ["Gross margin", "cm1"], ["Fulfilment margin", "cm2"],
                   ["Contribution margin", "cm3"], ["Net profit", "net"]]
              .filter(([, k]) => cp[k] != null)
              .map(([label, k]) => {
                const after = (target_state.waterfall.checkpoints || {})[k];
                return [label, money(cp[k], waterfall.currency), money(after, waterfall.currency),
                        after != null ? `${after - cp[k] >= 0 ? "+" : ""}${Math.round(after - cp[k]).toLocaleString()}` : "-"];
              })}
          />
          <div className="mt-6">
            <DataTable head={["Move included", "Adds to net profit / mo"]}
                       rows={(target_state.levers_applied || []).map((l) => [l.label, money(l.delta_vs_today, waterfall.currency)])} />
          </div>
          {target_state.lever_dropped_as_overlapping && (
            <p className="mt-4 font-mono-tech text-[11px] text-[#666] leading-relaxed">
              <strong>Excluded: {target_state.lever_dropped_as_overlapping.label}.</strong>{" "}
              {target_state.lever_dropped_as_overlapping.reason}
            </p>
          )}
          <Reads bullets={section_reads.target_state} />
        </Section>
      )}

      {!!(margin_bridge.steps && margin_bridge.steps.length) && (
        <Section
          testid="audit-bridge-section"
          tag="What we can prove it is worth"
          title={section_claims.bridge || "From today's net to where these levers land"}
          subtitle="This counts only what was diagnosed and sized from your own figures - narrower than the plan above, and firmer. Levers moving the same dollar are collapsed to the largest; the ones dropped are shown struck through."
        >
          <DataTable
            head={["Step", "Lever", "Adds to net profit / mo", "Basis", "Effort", "Running net profit"]}
            rows={[["Opening", "Net today", "-", "measured", "-", money(margin_bridge.opening_net, waterfall.currency)]]
              .concat((margin_bridge.steps || []).map((stp, i) => [
                String(i + 1).padStart(2, "0"), stp.lever_title || stp.title,
                `+${money(stp.impact_aud_monthly, waterfall.currency)}`,
                stp.basis || "-", stp.effort || "-", money(stp.running_net, waterfall.currency),
              ]))
              .concat((margin_bridge.excluded_as_overlapping || []).map((e) => [
                "--", <s key="t">{e.lever_title || e.title}</s>,
                <s key="i">{e.impact_aud_monthly ? `+${money(e.impact_aud_monthly, waterfall.currency)}` : "-"}</s>,
                <em key="r">{e.reason}</em>, "-", <em key="n">not counted</em>,
              ]))
              .concat([["Closing", "If all are actioned",
                        `+${money(margin_bridge.total_identified, waterfall.currency)}`, "", "",
                        money(margin_bridge.closing_net, waterfall.currency)]])}
          />
          <Reads bullets={section_reads.bridge} />
        </Section>
      )}

      {!!(priorities.ranked && priorities.ranked.length) && (
        <Section
          testid="audit-sequence-section"
          tag="What to do, in what order"
          title={section_claims.sequence || "Ordered by return per unit of effort"}
          subtitle="Severity says how bad something is. This says what pays most for the weeks you have. A smaller lever deliverable in days outranks a larger one that needs a quarter."
        >
          {[["quick_wins", "Do first - high impact, low effort"],
            ["major_initiatives", "Plan - high impact, heavier lift"],
            ["fill_ins", "Fill in - lower impact, cheap to do"],
            ["deprioritise", "Leave - low impact, high effort"]]
            .filter(([k]) => (priorities[k] || []).length)
            .map(([k, label]) => (
              <div key={k} className="mb-8 last:mb-0">
                <div className="tick mb-4">{label}</div>
                <DataTable
                  head={["Lever", "Adds to net profit / mo", "Share of total", "Effort"]}
                  rows={(priorities[k] || []).map((r) => [
                    r.lever_title || r.title, money(r.impact_aud_monthly, waterfall.currency),
                    `${r.share_of_identified_pct}%`, r.effort || "-",
                  ])}
                />
              </div>
            ))}
          {!!(priorities.unsized && priorities.unsized.length) && (
            <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
              <div className="tick mb-4">Carried without a dollar figure</div>
              <p className="mb-4 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
                Judgement calls, not zeros. Each states why the data cannot size it.
              </p>
              <DataTable
                head={["Finding", "Why it carries no figure"]}
                rows={priorities.unsized.map((u) => [u.lever_title || u.title, u.unavailable_reason || ""])}
              />
            </div>
          )}
        </Section>
      )}

      {(operator_note || verdict || executive_summary) && (
        <section className="card-cream p-8 md:p-10 border-l-4 border-l-[#9a3412]" data-testid="audit-report-operator-note">
          <div className="tick mb-6 text-[#9a3412]">The analyst&apos;s read</div>
          {operator_note && (
            <p className="font-serif-display text-xl md:text-2xl text-[#171717] leading-[1.4] max-w-3xl whitespace-pre-line">{operator_note}</p>
          )}
          {(verdict || executive_summary) && (
            <p className="mt-6 font-mono-tech text-[12px] text-[#404040] leading-relaxed max-w-3xl">
              {verdict || executive_summary}
            </p>
          )}
        </section>
      )}

      {decisions_for_call.length > 0 && (
          <div className="mt-8 pt-6 border-t border-[#ddd8ce]">
            <div className="tick mb-4">What we need you to decide</div>
            <ol className="space-y-3">
              {decisions_for_call.map((d, i) => (
                <li key={i} className="flex gap-4 font-mono-tech text-[12px] text-[#404040] leading-relaxed">
                  <span className="text-[#171717] font-medium shrink-0">{i + 1}</span><span>{d.decision}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>

      {decisions_for_call.length > 0 && (
      <Section
          testid="audit-decisions-for-call"
          tag="For the call"
          title="What gets decided, not just diagnosed"
          subtitle="Each decision below traces to a specific finding above — the call is where these get resolved, not re-explained."
        >
          <div className="space-y-3">
            {decisions_for_call.map((d, i) => {
              const linked = findings.find((f) => f.code === d.finding_code);
              return (
                <div key={i} className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-4 flex items-start gap-4" data-testid={`audit-decision-${i}`}>
                  <span className="tick text-[#9a3412] shrink-0 mt-0.5">{i + 1}</span>
                  <div>
                    <p className="font-mono-tech text-[13px] text-[#171717] leading-relaxed">{d.decision}</p>
                    {linked && (
                      <p className="mt-1.5 font-mono-tech text-[11px] text-[#525252]">
                        From <span className="text-[#9a3412]">{linked.code}</span> · {linked.title}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <Section testid="audit-method" tag="Method" title="How this audit is built">
        <p className="font-mono-tech text-[13px] text-[#404040] leading-relaxed mb-4">
          This audit is built entirely from the figures you submitted in the intake form. Margin Lens does not require,
          request, or independently verify bank statements, accounting exports, or ad-platform reports — the precision
          of the analysis is bounded by the precision of what you were able to share, by design.
          {competitor_domains.length > 0 && (
            <> Competitor figures above were read from {competitor_domains.join(", ")}'s public product feeds at the time
            this audit ran — the same deterministic read the free sweep runs, with no separate model call per competitor.</>
          )}
        </p>
        {gaps.length > 0 && (
          <div className="space-y-2">
            <div className="tick mb-1 text-[#9a3412]">Data gaps in this run</div>
            {gaps.map((g, i) => (
              <div key={i} className="flex gap-2 font-mono-tech text-[12px] text-[#525252] py-1.5 border-b border-[#e8e4df] last:border-0">
                <span className="text-[#9a3412] shrink-0">—</span>
                <span>{g}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <MetricDefinitions scope="audit" />
    </div>
  );
}
